import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Select } from 'ksnm-common-ui/lib/Select';
import { sortBy, uniqBy } from 'lodash-es';

import { controlApplicabilityStore } from '@stores/dictionaryStores/controlApplicabilityStore/controlApplicabilityStore';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';

import styles from './Filter.module.scss';

export const Filter = observer((): JSX.Element => {
  const { controlApplicability, setFilters, filters, setSelectNode, setSelectedKeys } = controlApplicabilityStore;
  const { reportGroups } = reportGroupsStore;
  const { organizations } = organizationsStore;

  const { reportGroup, organization } = filters;

  const onClear = useCallback(() => {
    setFilters({});
    setSelectNode(null);
    setSelectedKeys([]);
  }, [setFilters, setSelectNode, setSelectedKeys]);

  const onChangeReportGroup = useCallback(
    (v) => {
      setFilters({ ...filters, reportGroup: v });
      setSelectNode(null);
      setSelectedKeys([]);
    },
    [filters, setFilters, setSelectNode, setSelectedKeys],
  );

  const onChangeOrganization = useCallback(
    (v) => {
      setFilters({ ...filters, organization: v });
      setSelectNode(null);
      setSelectedKeys([]);
    },
    [filters, setFilters, setSelectNode, setSelectedKeys],
  );

  const reportGroupItems = useMemo(
    () =>
      sortBy(
        uniqBy(
          controlApplicability.filter((item) => {
            if (organization && item.orgCode !== organization) {
              return false;
            }
            return !!item.repGroupCode;
          }),
          'repGroupCode',
        ).map((group) => {
          const groupName = reportGroups.find((item) => item.code === group.repGroupCode)?.name;

          return {
            id: group.repGroupCode,
            name: `${group.repGroupCode} - ${groupName}`,
          };
        }),
        'name',
      ),
    [controlApplicability, organization, reportGroups],
  );

  const organizationItems = useMemo(
    () =>
      sortBy(
        uniqBy(
          controlApplicability.filter((item) => {
            if (reportGroup && item.repGroupCode !== reportGroup) {
              return false;
            }
            return !!item.orgCode;
          }),
          'orgCode',
        ).map((group) => {
          const organizationName = organizations.find((item) => item.code === group.orgCode)?.name;

          return {
            id: group.orgCode,
            name: `${group.orgCode} - ${organizationName}`,
          };
        }),
        'name',
      ),
    [controlApplicability, organizations, reportGroup],
  );

  return (
    <div className={styles.designFilters}>
      <div className={styles.items}>
        <Select
          items={organizationItems}
          placeholder="Организация"
          value={filters.organization}
          onChange={onChangeOrganization}
          size="small"
          showSearch
          className={styles.select}
        />
        <Select
          items={reportGroupItems}
          placeholder="Группа отчета"
          value={filters.reportGroup}
          onChange={onChangeReportGroup}
          size="small"
          showSearch
          className={styles.select}
        />
      </div>

      <Button onClick={onClear} disabled={!(reportGroup || organization)} icon={<Icon name="stop" />}>
        Сбросить фильтры
      </Button>
    </div>
  );
});
